import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import {
  createSubscription,
  fetchSubscriptionById,
  updateSubscription,
  fetchInstitutes,
  fetchServices,
  fetchPlans
} from '../../../services/salesSubscriptionService';

const AddEditSubscription = () => {
  const navigate = useNavigate();
  const { id, institute_id } = useParams(); // Subscription ID for editing
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Define form state
  const [formData, setFormData] = useState<{
    sales_institute_id: string;
    service_id: string;
    plan_id: string;
    start_date: string;
    end_date: string;
    status: string;
  }>({
    sales_institute_id: '',
    service_id: '',
    plan_id: '',
    start_date: new Date().toISOString().split('T')[0], // Default: Today
    end_date: '',
    status: 'A' // Default Active
  });

  const [institutes, setInstitutes] = useState<
    { sales_institute_id: string; institute_name: string }[]
  >([]);
  const [services, setServices] = useState<
    { service_id: string; service_name: string }[]
  >([]);
  const [plans, setPlans] = useState<{ plan_id: string; plan_name: string }[]>(
    []
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch dropdown options
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [institutesData, servicesData, plansData] = await Promise.all([
          fetchInstitutes(institute_id),
          fetchServices(),
          fetchPlans()
        ]);
        setInstitutes(
          Array.isArray(institutesData) ? institutesData : [institutesData]
        );
        setServices(servicesData);
        setPlans(plansData);
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        setError('Failed to load dropdown data.');
      } finally {
        setLoading(false);
      }
    };
    loadDropdowns();
  }, []);

  // Fetch existing subscription if editing
  useEffect(() => {
    if (id) {
      setLoading(true);
      const loadSubscription = async () => {
        try {
          const subscription = await fetchSubscriptionById(id);
          setFormData({
            sales_institute_id: subscription.sales_institute_id.toString(),
            service_id: subscription.service_id.toString(),
            plan_id: subscription.plan_id.toString(),
            start_date: subscription.start_date.split('T')[0],
            end_date: subscription.end_date.split('T')[0],
            status: subscription.CDC_FLAG
          });
        } catch (error) {
          setError('Failed to load subscription details.');
          console.error('Error fetching subscription:', error);
        } finally {
          setLoading(false);
        }
      };
      loadSubscription();
    }
  }, [id]);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    setFormData(prevState => ({
      ...prevState,
      [target.name]: target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.end_date <= formData.start_date) {
      alert('End date must be after start date!');
      return;
    }

    try {
      if (id) {
        await updateSubscription(id, formData);
        alert('Subscription updated successfully');
      } else {
        await createSubscription(formData);
        alert('Subscription added successfully');
      }
      navigate('/sales/subscription/all-subscription');
    } catch (error) {
      console.error('Error submitting subscription:', error);
    }
  };

  return (
    <div className="container">
      <h2>{id ? 'Edit' : 'Add'} Subscription</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading dropdowns...</span>
          </Spinner>
          <p>Loading data...</p>
        </div>
      ) : (
        <Form onSubmit={handleSubmit}>
          {/* Institute Selection */}
          <Form.Group>
            <Form.Label>Institute</Form.Label>
            <Form.Select
              name="sales_institute_id"
              value={formData.sales_institute_id}
              onChange={handleChange}
              required
              disabled={submitting}
            >
              <option value="">Select Institute</option>
              {institutes.map(institute => (
                <option
                  key={institute.sales_institute_id}
                  value={institute.sales_institute_id}
                >
                  {institute.institute_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Service Selection */}
          <Form.Group>
            <Form.Label>Service</Form.Label>
            <Form.Select
              name="service_id"
              value={formData.service_id}
              onChange={handleChange}
              required
              disabled={submitting}
            >
              <option value="">Select Service</option>
              {services.map(service => (
                <option key={service.service_id} value={service.service_id}>
                  {service.service_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Plan Selection */}
          <Form.Group>
            <Form.Label>Plan</Form.Label>
            <Form.Select
              name="plan_id"
              value={formData.plan_id}
              onChange={handleChange}
              required
              disabled={submitting}
            >
              <option value="">Select Plan</option>
              {plans.map(plan => (
                <option key={plan.plan_id} value={plan.plan_id}>
                  {plan.plan_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Start Date */}
          <Form.Group>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </Form.Group>

          {/* End Date */}
          <Form.Group>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </Form.Group>

          {/* Status Field */}
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              disabled={submitting}
            >
              <option value="A">Active</option>
              <option value="I">Inactive</option>
              <option value="D">Deleted</option>
            </Form.Select>
          </Form.Group>

          {/* Submit Button */}
          <Button type="submit" className="mt-3" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              `${id ? 'Update' : 'Add'} Subscription`
            )}
          </Button>
        </Form>
      )}
    </div>
  );
};

export default AddEditSubscription;
